/**
 * Light / Dark / System inside the avatar menu — the same three modes
 * `ThemeContext` already supports, one click instead of three (nav spec §4.5).
 *
 * `menuitemradio` inside `group`, not `SegmentedControl`: a `radiogroup` is not
 * a valid child of `role="menu"`, and a menu item that closed the menu on click
 * would make comparing two modes a chore. Choosing a mode leaves the menu open.
 */
import React, { useId } from 'react';

import { useTheme } from '@/hooks/useTheme';
import type { ThemeMode } from '@/hooks/useTheme';

const MODES: { value: ThemeMode; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

/**
 * The Appearance row: the label, then the three-mode control on its own line
 * so it fits the menu's width (the two side by side overflow a 224px menu).
 */
const AppearanceRow: React.FC = () => {
  const { themeMode, setThemeMode } = useTheme();
  const labelId = useId();
  return (
    <div className="px-4 py-2 text-sm">
      <span id={labelId} className="text-foreground">
        Appearance
      </span>
      <div
        role="group"
        aria-labelledby={labelId}
        className="border-border bg-muted mt-1 flex items-center gap-0.5 rounded-md border p-0.5"
      >
        {MODES.map(mode => {
          const checked = mode.value === themeMode;
          return (
            <button
              key={mode.value}
              type="button"
              role="menuitemradio"
              aria-checked={checked}
              onClick={() => setThemeMode(mode.value)}
              className={`flex-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                checked
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {mode.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AppearanceRow;
